import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@vitness/theme';

/**
 * Tabbed sections built on Radix Tabs — used in detail drawers and settings
 * panels. Implements the WAI-ARIA tabs pattern: arrow-key navigation across the
 * tablist, automatic activation, and proper `aria-selected`/`aria-controls`
 * wiring. The active tab indicator is the theme accent (`--vn-accent-*`).
 *
 * Compose from: `Tabs` (root), `TabsList`, `TabsTrigger`, `TabsContent`.
 */
export const Tabs = TabsPrimitive.Root;
export type TabsProps = ComponentPropsWithoutRef<typeof TabsPrimitive.Root>;

export const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(function TabsList({ className, ...props }, ref) {
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'inline-flex items-center gap-vn-1 border-b border-border font-body',
        className,
      )}
      {...props}
    />
  );
});

export const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(function TabsTrigger({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'relative -mb-px inline-flex items-center justify-center whitespace-nowrap',
        'border-b-2 border-transparent px-vn-3 py-vn-2 text-sm font-medium',
        'text-text-secondary transition-colors',
        'hover:text-text-primary',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vn-border-strong)]',
        'data-[state=active]:border-[var(--vn-text-primary)] data-[state=active]:text-text-primary',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    />
  );
});

export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(function TabsContent({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        'mt-vn-3 font-body text-sm text-text-primary',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vn-border-strong)]',
        className,
      )}
      {...props}
    />
  );
});
